import { useState, useEffect } from 'react'
import { usePreferences } from '../contexts/PreferencesContext'
import { 
    Plus, 
    Edit3, 
    Trash2, 
    Copy, 
    Grid, 
    Monitor,
    Play,
    Pause,
    Settings,
    Eye,
    MoreVertical
} from 'lucide-react'
import toast from 'react-hot-toast'

const ScreenManager = ({ 
    isOpen, 
    onClose, 
    onEditScreen, 
    onViewScreen,
    availableCameras = [] 
}) => {
    const { 
        preferences,
        createScreen,
        updateScreen,
        deleteScreen,
        setActiveScreen,
        getScreens,
        getMostUsedScreens,
        updateAutoRotation
    } = usePreferences()

    const [activeTab, setActiveTab] = useState('screens')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
    const [selectedScreens, setSelectedScreens] = useState([])

    const screens = getScreens()
    const mostUsedScreens = getMostUsedScreens(3)
    const activeScreen = preferences.screens.activeScreenId

    const handleCreateScreen = (template = null) => {
        const screenConfig = {
            name: `Screen ${screens.length + 1}`,
            template: template || 'custom',
            grid: template ? getTemplateConfig(template) : { columns: 2, rows: 2 },
            cameras: []
        }
        
        const screenId = createScreen(screenConfig)
        toast.success('Screen created successfully')
        
        if (onEditScreen) {
            onEditScreen(screenId)
        }
    }

    const handleCloneScreen = (screen) => {
        const clonedConfig = {
            ...screen,
            name: `${screen.name} (Copy)`,
            id: undefined // Let createScreen generate new ID
        }
        
        const screenId = createScreen(clonedConfig)
        toast.success('Screen cloned successfully')
    }

    const handleDeleteScreen = (screenId) => {
        if (deleteScreen(screenId)) {
            toast.success('Screen deleted successfully')
            setShowDeleteConfirm(null)
        } else {
            toast.error('Failed to delete screen')
        }
    }

    const handleSetActive = (screenId) => {
        if (setActiveScreen(screenId)) {
            toast.success('Active screen changed')
            if (onViewScreen) {
                onViewScreen(screenId)
            }
        }
    }

    const getTemplateConfig = (template) => {
        const templates = {
            'single': { columns: 1, rows: 1, type: 'symmetric' },
            'quad': { columns: 2, rows: 2, type: 'symmetric' },
            'six': { columns: 3, rows: 2, type: 'symmetric' },
            'nine': { columns: 3, rows: 3, type: 'symmetric' },
            'hero': { columns: 3, rows: 3, type: 'hero-plus' }
        }
        return templates[template] || { columns: 2, rows: 2, type: 'symmetric' }
    }

    const toggleAutoRotation = () => {
        const newEnabled = !preferences.screens.autoRotation.enabled
        updateAutoRotation({ enabled: newEnabled })
        toast.success(`Auto-rotation ${newEnabled ? 'enabled' : 'disabled'}`)
    }

    const updateRotationInterval = (interval) => {
        updateAutoRotation({ interval: parseInt(interval) })
        toast.success('Rotation interval updated')
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Screen Management
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        ×
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('screens')}
                        className={`px-6 py-3 font-medium ${
                            activeTab === 'screens'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                        }`}
                    >
                        <Grid className="w-4 h-4 inline mr-2" />
                        Screens ({screens.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('rotation')}
                        className={`px-6 py-3 font-medium ${
                            activeTab === 'rotation'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                        }`}
                    >
                        <Monitor className="w-4 h-4 inline mr-2" />
                        Auto-Rotation
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {activeTab === 'screens' && (
                        <div>
                            {/* Quick Actions */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                                    Quick Start
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <button
                                        onClick={() => handleCreateScreen('single')}
                                        className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded mb-2 mx-auto"></div>
                                        <div className="text-sm font-medium">Single</div>
                                        <div className="text-xs text-gray-500">1×1</div>
                                    </button>
                                    <button
                                        onClick={() => handleCreateScreen('quad')}
                                        className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <div className="grid grid-cols-2 gap-1 w-8 h-8 mx-auto mb-2">
                                            {[...Array(4)].map((_, i) => (
                                                <div key={i} className="bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            ))}
                                        </div>
                                        <div className="text-sm font-medium">Quad</div>
                                        <div className="text-xs text-gray-500">2×2</div>
                                    </button>
                                    <button
                                        onClick={() => handleCreateScreen('six')}
                                        className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <div className="grid grid-cols-3 gap-1 w-8 h-8 mx-auto mb-2">
                                            {[...Array(6)].map((_, i) => (
                                                <div key={i} className="bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            ))}
                                        </div>
                                        <div className="text-sm font-medium">Six</div>
                                        <div className="text-xs text-gray-500">3×2</div>
                                    </button>
                                    <button
                                        onClick={() => handleCreateScreen('nine')}
                                        className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        <div className="grid grid-cols-3 gap-1 w-8 h-8 mx-auto mb-2">
                                            {[...Array(9)].map((_, i) => (
                                                <div key={i} className="bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            ))}
                                        </div>
                                        <div className="text-sm font-medium">Nine</div>
                                        <div className="text-xs text-gray-500">3×3</div>
                                    </button>
                                    <button
                                        onClick={() => handleCreateScreen()}
                                        className="p-4 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-blue-600 dark:text-blue-400"
                                    >
                                        <Plus className="w-8 h-8 mx-auto mb-2" />
                                        <div className="text-sm font-medium">Custom</div>
                                        <div className="text-xs">Your design</div>
                                    </button>
                                </div>
                            </div>

                            {/* Most Used Screens */}
                            {mostUsedScreens.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                                        Most Used
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {mostUsedScreens.map(screen => (
                                            <ScreenCard
                                                key={screen.id}
                                                screen={screen}
                                                isActive={activeScreen === screen.id}
                                                onSetActive={handleSetActive}
                                                onEdit={onEditScreen}
                                                onClone={handleCloneScreen}
                                                onDelete={(screenId) => setShowDeleteConfirm(screenId)}
                                                onView={onViewScreen}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* All Screens */}
                            <div>
                                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                                    All Screens
                                </h3>
                                {screens.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <Grid className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>No screens created yet</p>
                                        <p className="text-sm">Create your first screen using the templates above</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {screens.map(screen => (
                                            <ScreenCard
                                                key={screen.id}
                                                screen={screen}
                                                isActive={activeScreen === screen.id}
                                                onSetActive={handleSetActive}
                                                onEdit={onEditScreen}
                                                onClone={handleCloneScreen}
                                                onDelete={(screenId) => setShowDeleteConfirm(screenId)}
                                                onView={onViewScreen}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'rotation' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                                    Auto-Rotation Settings
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div>
                                            <div className="font-medium text-gray-900 dark:text-white">
                                                Enable Auto-Rotation
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                Automatically cycle through screens in fullscreen mode
                                            </div>
                                        </div>
                                        <button
                                            onClick={toggleAutoRotation}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
                                                preferences.screens.autoRotation.enabled
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                                            }`}
                                        >
                                            {preferences.screens.autoRotation.enabled ? (
                                                <>
                                                    <Pause className="w-4 h-4" />
                                                    Enabled
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="w-4 h-4" />
                                                    Disabled
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Rotation Interval
                                            </label>
                                            <select
                                                value={preferences.screens.autoRotation.interval}
                                                onChange={(e) => updateRotationInterval(e.target.value)}
                                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            >
                                                <option value="10">10 seconds</option>
                                                <option value="15">15 seconds</option>
                                                <option value="30">30 seconds</option>
                                                <option value="60">1 minute</option>
                                                <option value="120">2 minutes</option>
                                                <option value="300">5 minutes</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Screens to Rotate ({preferences.screens.autoRotation.screenOrder.length})
                                            </label>
                                            <button
                                                onClick={() => {/* TODO: Open screen order modal */}}
                                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left"
                                            >
                                                {preferences.screens.autoRotation.screenOrder.length === 0
                                                    ? 'Select screens...'
                                                    : `${preferences.screens.autoRotation.screenOrder.length} screens selected`
                                                }
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={preferences.screens.autoRotation.pauseOnActivity}
                                                onChange={(e) => updateAutoRotation({ pauseOnActivity: e.target.checked })}
                                                className="rounded"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                Pause rotation when motion is detected
                                            </span>
                                        </label>

                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={preferences.screens.autoRotation.adaptiveTiming}
                                                onChange={(e) => updateAutoRotation({ adaptiveTiming: e.target.checked })}
                                                className="rounded"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                Adaptive timing (adjust based on screen complexity)
                                            </span>
                                        </label>
                                    </div>

                                    {/* Screen Rotation Order & Durations */}
                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                                            Screen Rotation Order & Durations
                                        </h3>
                                        
                                        {screens.length > 0 ? (
                                            <div className="space-y-3">
                                                {screens.map((screen, index) => (
                                                    <ScreenRotationItem
                                                        key={screen.id}
                                                        screen={screen}
                                                        isInRotation={preferences.screens.autoRotation.screenOrder.includes(screen.id)}
                                                        duration={(preferences.screens.autoRotation.screenDurations || {})[screen.id] || preferences.screens.autoRotation.interval}
                                                        onToggleRotation={(screenId, include) => {
                                                            const currentOrder = preferences.screens.autoRotation.screenOrder
                                                            let newOrder
                                                            if (include) {
                                                                newOrder = [...currentOrder, screenId]
                                                            } else {
                                                                newOrder = currentOrder.filter(id => id !== screenId)
                                                            }
                                                            updateAutoRotation({ screenOrder: newOrder })
                                                        }}
                                                        onDurationChange={(screenId, duration) => {
                                                            updateAutoRotation({
                                                                screenDurations: {
                                                                    ...(preferences.screens.autoRotation.screenDurations || {}),
                                                                    [screenId]: parseInt(duration)
                                                                }
                                                            })
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                                                <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">No screens available for rotation</p>
                                                <p className="text-xs">Create screens first to set up rotation</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Delete Screen
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 mb-6">
                                Are you sure you want to delete this screen? This action cannot be undone.
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDeleteScreen(showDeleteConfirm)}
                                    className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

// Screen Card Component
const ScreenCard = ({ 
    screen, 
    isActive, 
    onSetActive, 
    onEdit, 
    onClone, 
    onDelete, 
    onView 
}) => {
    const [showMenu, setShowMenu] = useState(false)

    return (
        <div className={`relative p-4 border-2 rounded-lg transition-all ${
            isActive 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
        }`}>
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                        {screen.name}
                    </h4>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        {screen.grid.columns}×{screen.grid.rows} • {screen.cameras.length} cameras
                    </div>
                    {screen.usageCount > 0 && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                            Used {screen.usageCount} times
                        </div>
                    )}
                </div>
                
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    {showMenu && (
                        <div className="absolute right-0 top-6 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg shadow-lg z-10 min-w-[140px]">
                            <button
                                onClick={() => {
                                    onView?.(screen.id)
                                    setShowMenu(false)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2"
                            >
                                <Eye className="w-4 h-4" />
                                View
                            </button>
                            <button
                                onClick={() => {
                                    onEdit?.(screen.id)
                                    setShowMenu(false)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2"
                            >
                                <Edit3 className="w-4 h-4" />
                                Edit
                            </button>
                            <button
                                onClick={() => {
                                    onClone?.(screen)
                                    setShowMenu(false)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2"
                            >
                                <Copy className="w-4 h-4" />
                                Clone
                            </button>
                            <button
                                onClick={() => {
                                    onDelete?.(screen.id)
                                    setShowMenu(false)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2 text-red-600"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Screen Preview */}
            <div className="mb-3">
                <div className={`grid gap-1 w-full h-16 bg-gray-100 dark:bg-gray-700 rounded p-2`}
                     style={{ 
                         gridTemplateColumns: `repeat(${screen.grid.columns}, 1fr)`,
                         gridTemplateRows: `repeat(${screen.grid.rows}, 1fr)`
                     }}>
                    {Array.from({ length: screen.grid.columns * screen.grid.rows }).map((_, index) => {
                        const hasCamera = screen.cameras[index]
                        return (
                            <div
                                key={index}
                                className={`rounded-sm ${
                                    hasCamera 
                                        ? 'bg-blue-200 dark:bg-blue-600' 
                                        : 'bg-gray-200 dark:bg-gray-600'
                                }`}
                            />
                        )
                    })}
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
                <button
                    onClick={() => onSetActive(screen.id)}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg ${
                        isActive
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                    }`}
                >
                    {isActive ? 'Active' : 'Use'}
                </button>
                <button
                    onClick={() => onEdit?.(screen.id)}
                    className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                >
                    <Edit3 className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}

// Screen Rotation Item Component
const ScreenRotationItem = ({ 
    screen, 
    isInRotation, 
    duration, 
    onToggleRotation, 
    onDurationChange 
}) => {
    return (
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-3">
                <input
                    type="checkbox"
                    checked={isInRotation}
                    onChange={(e) => onToggleRotation(screen.id, e.target.checked)}
                    className="rounded"
                />
                <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                        {screen.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        {screen.grid.columns}×{screen.grid.rows} • {screen.cameras.length} cameras
                    </div>
                </div>
            </div>
            
            {isInRotation && (
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                        Duration:
                    </label>
                    <select
                        value={duration}
                        onChange={(e) => onDurationChange(screen.id, e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                        <option value="5">5 seconds</option>
                        <option value="10">10 seconds</option>
                        <option value="15">15 seconds</option>
                        <option value="30">30 seconds</option>
                        <option value="45">45 seconds</option>
                        <option value="60">1 minute</option>
                        <option value="90">1.5 minutes</option>
                        <option value="120">2 minutes</option>
                        <option value="180">3 minutes</option>
                        <option value="300">5 minutes</option>
                    </select>
                </div>
            )}
        </div>
    )
}

export default ScreenManager